import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { client_state } from "./ClientState";
import { canDownloadFiles } from "../widgets/instructions/fileUpload";
import uuid = require("../state/uuid");
import { isKeyProperty } from "./isKey";
import { validateText } from "./textValidation";
import { getInitializedEntryWithKey } from "../state/Collection";
import { getGuiText } from "../guiModel";
import { iCollection } from "../state/Collection";

function get_parent_location(node: api_cs.Cnode): "local" | "remote" {
	switch (node.properties.location.state.name) {
		case "local": {
			const $ = node.properties.location.state.node;
			return $.properties.parent_location.state.name;
		}
		case "remote":
			return "remote";
		default:
			unexpectedState(node.properties.location.state);
	}
}

function createNode(
	definition: api_app.Cnode__application,
	location: api_cs.Tnode["location"],
	init = true
): api_cs.Tnode {
	let todo: api_cs.Tnode["todo"];
	switch (definition.properties.todo_definition.properties.todo.state.name) {
		case "no":
			todo = ["no", {}];
			break;
		case "yes":
			todo = ["yes", { for: ["someone else", {}] }];
			break;
		default:
			unexpectedState(definition.properties.todo_definition.properties.todo.state);
	}
	const attributes: api_cs.Tnode["attributes"] = {};

	if (init) {
		for (const [key, attribute] of definition.properties.attributes) {
			switch (attribute.properties.type.state.name) {
				case "property": {
					const property = attribute.properties.type.state.node;
					const kind = property.properties.type.state.node.output.value_source();
					switch (kind.variant.name) {
						case "base": {
							switch (property.properties.type.state.name) {
								case "collection":
									attributes[key] = createCollectionAttribute(property.properties.type.state.node, "local");
									break;
								case "file":
									attributes[key] = createFileAttribute(property.properties.type.state.node, "local");
									break;
								case "group":
									attributes[key] = createGroupAttribute(property.properties.type.state.node, "local", "local");
									break;
								case "number":
									attributes[key] = createNumberAttribute(property.properties.type.state.node, "local");
									break;
								case "state group":
									attributes[key] = createStategroupAttribute(property.properties.type.state.node, "local", "local");
									break;
								case "text":
									attributes[key] = createTextAttribute(property.properties.type.state.node, "local");
									break;
								default:
									unexpectedState(property.properties.type.state);
							}
							break;
						}
						case "derived":
							break;
						default:
							unexpectedState(kind.variant);
					}
					break;
				}
				case "action":
					break;
				case "command":
					break;
				case "reference set":
					break;
				default:
					unexpectedState(attribute.properties.type.state);
			}
		}
	}

	return {
		location: location,
		todo: todo,
		"read permitted": ["yes", {}],
		"can be deleted": ["yes", {}],
		modified: ["no", {}],
		"is valid": ["yes", {}],
		"is busy discarding": ["no", {}],
		"is busy saving": ["no", {}],
		attributes: attributes,
		queries: {},
		"report types": {},
	};
}

export function createEmptyRemoteNode(definition: api_app.Cnode__application): api_cs.Tnode {
	return createNode(
		definition,
		[
			"remote",
			{
				"edit mode": ["off", {}],
			},
		],
		false
	);
}

export function createLocalNode(
	definition: api_app.Cnode__application,
	parent_location: api_cs.Tlocal__location__node["parent location"],
	init = true
): api_cs.Tnode {
	return createNode(
		definition,
		[
			"local",
			{
				"parent location": parent_location,
			},
		],
		init
	);
}

function createTextAttribute(
	definition: api_app.Ctext__type__property,
	location: "remote" | "local",
	backend_value?: string
): api_cs.Tattributes {
	let is_set: api_cs.Ttext__type__property["is set"] = ["no", {}];
	let backend_known: api_cs.Ttext__type__property["backend known"] = ["no", {}];
	let is_editable: api_cs.Ttext__type__property["is editable"] = ["no", {}];
	let is_valid: api_cs.Ttext__type__property["is valid"] = ["yes", {}];
	let validation_result: api_cs.Ttext__type__property["validation result"] = ["success", {}];
	let reference: api_cs.Ttext__type__property["navigable"] = definition.properties.has_reference.switch<
		api_cs.Ttext__type__property["navigable"]
	>({
		no: ["no", {}],
		yes: ($) => [
			"yes",
			{
				"resolve status": {
					status: ["unresolved", {}],
					"validation result": ["success", {}],
				},
				type: $.properties.behaviour.properties.type.switch({
					mandatory: ["mandatory", {}],
					optional: ["optional", {}],
				}),
				rules: {
					rules: $.properties.rules.properties.rules.map(() => ({
						"is valid": "no",
						"resolve status": {
							status: ["unresolved", {}],
							"validation result": ["success", {}],
						},
					})),
				},
				queries: {},
			},
		],
	});

	const gui_definition = getGuiText(definition);
	switch (location) {
		case "local": {
			if (gui_definition) {
				let reference_not_resolved = false;
				let text_does_not_match_pattern: api_cs.Tno__is_valid__text__type__property["text does not match pattern"] = [
					"no",
					{},
				];
				let invalid_rules = false;
				switch (gui_definition.properties.validation.state.name) {
					case "none":
						break;
					case "model": {
						const model_validation = gui_definition.properties.validation.state.node;
						const rules = model_validation.inferences.text_with_validation().properties.rules;
						const text_is_valid = validateText("", rules);
						if (!text_is_valid) {
							text_does_not_match_pattern = [
								"yes",
								{
									pattern: rules.properties.regular_expression,
								},
							];
							validation_result = ["failure", {}];
						}
						break;
					}
					case "custom": {
						const custom_validation = gui_definition.properties.validation.state.node;
						const rules = custom_validation.properties.rules;
						const text_is_valid = validateText("", rules);
						if (!text_is_valid) {
							text_does_not_match_pattern = [
								"yes",
								{
									pattern: rules.properties.regular_expression,
								},
							];
							validation_result = ["failure", {}];
						}
						break;
					}
					default:
						unexpectedState(gui_definition.properties.validation.state);
				}
				switch (gui_definition.properties.navigable.state.name) {
					case "yes": {
						const navigable = gui_definition.properties.navigable.state.node;
						reference = [
							"yes",
							{
								"resolve status": {
									status: ["unresolved", {}],
									"validation result": navigable.properties.validate.switch<
										api_cs.Tresolve_status["validation result"]
									>({
										yes: () => {
											reference_not_resolved = true;
											return ["failure", {}] as api_cs.Tresolve_status["validation result"];
										},
										no: ["success", {}],
									}),
								},
								type: navigable.inferences.reference().properties.behaviour.properties.type.switch({
									mandatory: ["mandatory", {}],
									optional: ["optional", {}],
								}),
								rules: {
									rules: navigable.properties.rules.properties.rules.map(($) => {
										return {
											"is valid": "no",
											"resolve status": {
												status: ["unresolved", {}],
												"validation result": $.properties.validate.switch<api_cs.Tresolve_status["validation result"]>({
													yes: () => {
														invalid_rules = true;
														return ["failure", {}] as api_cs.Tresolve_status["validation result"];
													},
													no: ["success", {}],
												}),
											},
										};
									}),
								},
								queries: {},
							},
						];
						break;
					}
					case "no":
						break;
					default:
						unexpectedState(gui_definition.properties.navigable.state);
				}
				if (reference_not_resolved || text_does_not_match_pattern[0] === "yes" || invalid_rules) {
					is_valid = [
						"no",
						{
							"reference not resolved": reference_not_resolved ? ["yes", {}] : ["no", {}],
							"referenced node not valid": ["no", {}],
							"text does not match pattern": text_does_not_match_pattern,
							"invalid rules": invalid_rules ? ["yes", {}] : ["no", {}],
							"key is not unique": ["no", {}],
						},
					];
				}
			}
			switch (definition.properties.type.state.name) {
				case "base":
					is_editable = [
						"yes",
						{
							modified: ["no", {}],
							"edit mode": ["off", {}],
						},
					];
					break;
				case "derived":
					break;
				default:
					unexpectedState(definition.properties.type.state);
			}
			break;
		}
		case "remote":
			if (backend_value !== undefined) {
				backend_known = [
					"yes",
					{
						value: backend_value,
					},
				];
				is_set = [
					"yes",
					{
						text: backend_value,
						by: ["engine", {}],
					},
				];
			}
			break;
	}

	return {
		type: [
			"property",
			{
				type: [
					"text",
					{
						"is editable": is_editable,
						"is set": is_set,
						"backend known": backend_known,
						"is valid": is_valid,
						"validation result": validation_result,
						navigable: reference,
						"is key": isKeyProperty(definition)
							? [
									"yes",
									{
										"key unique": ["yes", {}],
									},
							  ]
							: ["no", {}],
					},
				],
			},
		],
	};
}

function createNumberAttribute(
	definition: api_app.Cnumber__type__property,
	location: "remote" | "local"
): api_cs.Tattributes {
	let is_editable: api_cs.Tnumber__type__property["is editable"] = ["no", {}];
	let is_valid: api_cs.Tnumber__type__property["is valid"] = ["yes", {}];
	switch (definition.properties.type.state.name) {
		case "base": {
			const base = definition.properties.type.state.node;
			switch (base.properties.type.state.name) {
				case "simple":
					is_editable = [
						"yes",
						{
							modified: ["no", {}],
							"edit mode": ["off", {}],
						},
					];
					switch (location) {
						case "local":
							is_valid = [
								"no",
								{
									"exceeds maximum": "no",
									"exceeds minimum": "no",
									"not a number": "no",
									"not set": "yes",
									"should be positive": "no",
								},
							];
							break;
						case "remote":
							break;
						default:
							unexpectedState(location);
					}
					break;
				case "causal":
					break;
				default:
					unexpectedState(base.properties.type.state);
			}
			break;
		}
		case "derived":
			break;
		default:
			unexpectedState(definition.properties.type.state);
	}
	return {
		type: [
			"property",
			{
				type: [
					"number",
					{
						"is editable": is_editable,
						"is set": ["no", {}],
						"is valid": is_valid,
						"backend known": ["no", {}],
						"number type": definition.properties.set_type.switch({
							integer: ["integer", {}],
							positive: ["positive", {}],
						}),
						"numerical type properties": {
							initialized: "no",
						},
					},
				],
			},
		],
	};
}

function createFileAttribute(definition: api_app.Cfile, location: "remote" | "local"): api_cs.Tattributes {
	let is_valid: api_cs.Tfile__type__property["is valid"];
	let is_editable: api_cs.Tfile__type__property["is editable"];
	switch (definition.properties.type.state.name) {
		case "base": {
			switch (location) {
				case "local":
					is_valid = [
						"no",
						{
							"extension not valid": "no",
							"not set": "yes",
						},
					];
					is_editable = [
						"yes",
						{
							modified: ["no", {}],
							"edit mode": ["off", {}],
						},
					];
					break;
				case "remote":
					is_valid = ["yes", {}];
					is_editable = ["no", {}];
					break;
				default:
					unexpectedState(location);
			}
			break;
		}
		case "derived":
			is_valid = ["yes", {}];
			is_editable = ["no", {}];
			break;
		default:
			unexpectedState(definition.properties.type.state);
	}
	return {
		type: [
			"property",
			{
				type: [
					"file",
					{
						"is editable": is_editable,
						"backend known": ["no", {}],
						"is set": "no",
						"is valid": is_valid,
					},
				],
			},
		],
	};
}

function createGroupAttribute(
	definition: api_app.Cgroup__type__property,
	location: "remote" | "local",
	parent_location: "remote" | "local"
): api_cs.Tattributes {
	let group_node: api_cs.Tnode;
	switch (location) {
		case "remote":
			group_node = createEmptyRemoteNode(definition.properties.node);
			break;
		case "local":
			group_node = createLocalNode(definition.properties.node, [parent_location, {}]);
			break;
		default:
			unexpectedState(location);
	}
	return {
		type: [
			"property",
			{
				type: [
					"group",
					{
						node: group_node,
					},
				],
			},
		],
	};
}

function mapToOrderedDictionary_states<T>(
	stategroup: api_app.Cstate_group,
	map: (state: api_app.Cstates__state_group) => T
): { [key: string]: T } {
	const result: { [key: string]: T } = {};
	let state: api_app.Cstates__state_group | undefined = stategroup.properties.first_state.ref;
	while (state) {
		result[state.key] = map(state);
		switch (state.properties.has_successor.state.name) {
			case "yes":
				state = state.properties.has_successor.state.node.properties.successor.ref;
				break;
			case "no":
				state = undefined;
				break;
			default:
				unexpectedState(state.properties.has_successor.state);
		}
	}
	return result;
}

function createStategroupAttribute(
	definition: api_app.Cstate_group,
	location: "remote" | "local",
	parent_location: "remote" | "local",
	remote_state?: api_app.Cstates__state_group
): api_cs.Tattributes {
	return {
		type: [
			"property",
			{
				type: [
					"state group",
					{
						initialized: ["no", {}],
						"is set":
							remote_state !== undefined
								? [
										"yes",
										{
											state: remote_state.key,
											by: ["user", {}],
										},
								  ]
								: ["no", {}],
						"is modified": ["no", {}],
						"is valid": definition.properties.type.switch({
							base: [
								"no",
								{
									"invalid rules": "no",
									"no state selected": "yes",
								},
							],
							derived: ["yes", {}],
						}),
						"is editable": definition.properties.type.switch({
							base: [
								"yes",
								{
									"edit mode": ["off", {}],
								},
							],
							derived: ["no", {}],
						}),
						states: mapToOrderedDictionary_states<api_cs.Tstates__state_group__type__property>(definition, ($) => {
							if ($ === remote_state) {
								return {
									"is selected": ["yes", {}],
									"create permitted": ["yes", {}],
									location: ["remote", {}],
									style: { initialized: "no" },
									rules: {
										rules: $.properties.rules.properties.rules.map(($) => ({
											"is valid": "no",
											"resolve status": {
												status: ["unresolved", {}],
												"validation result": $.properties.ui.properties.has_validation.switch({
													yes: ["failure", {}],
													no: ["success", {}],
												}),
											},
										})),
									},
									node: createEmptyRemoteNode($.properties.node),
								};
							} else {
								return {
									"is selected": ["no", {}],
									location: ["local", {}],
									"create permitted": [location === "local" ? "yes" : "no", {}],
									style: { initialized: "no" },
									rules: {
										rules: $.properties.rules.properties.rules.map(($) => ({
											"is valid": "no",
											"resolve status": {
												status: ["unresolved", {}],
												"validation result": $.properties.ui.properties.has_validation.switch({
													yes: ["failure", {}],
													no: ["success", {}],
												}),
											},
										})),
									},
									node: createLocalNode($.properties.node, parent_location),
								};
							}
						}),
					},
				],
			},
		],
	};
}

function createCollectionAttribute(
	definition: api_app.Ccollection__type__property,
	location: "remote" | "local"
): api_cs.Tattributes {
	let is_editable: api_cs.Tcollection["is editable"];
	let has_ordered_graphs: api_cs.Tcollection["has ordered graphs"] = ["no", {}];
	switch (definition.properties.type.state.name) {
		case "base": {
			switch (location) {
				case "local":
					switch (client_state.properties.session.properties.status.state.name) {
						case "authenticated":
							is_editable = [
								"yes",
								{
									"edit mode": ["off", {}],
								},
							];
							break;
						case "not authenticated":
							is_editable = ["no", {}];
							break;
						default:
							unexpectedState(client_state.properties.session.properties.status.state);
					}
					break;
				case "remote":
					is_editable = ["no", {}];
					break;
				default:
					unexpectedState(location);
			}
			const ordered_graphs = definition.properties.graph_constraints.properties.graphs
				.toArray()
				.reduce<{ [key: string]: api_cs.Tordered_graphs }>((ordered_graphs, graph) => {
					switch (graph[1].properties.type.state.name) {
						case "ordered":
							ordered_graphs[graph[0]] = { "is valid": ["yes", {}] };
							break;
						case "acyclic":
							break;
						default:
							unexpectedState(graph[1].properties.type.state);
					}
					return ordered_graphs;
				}, {});
			if (Object.keys(ordered_graphs).length > 0) {
				has_ordered_graphs = [
					"yes",
					{
						"ordered graphs": ordered_graphs,
					},
				];
			}
			break;
		}
		case "derived":
			is_editable = ["no", {}];
			break;
		default:
			unexpectedState(definition.properties.type.state);
	}
	return {
		type: [
			"property",
			{
				type: [
					"collection",
					{
						"is valid": ["yes", {}],
						"can create entry": definition.properties.type.switch({
							derived: "no",
							base: "yes",
						}),
						"can download files": canDownloadFiles(definition) ? "yes" : "no",
						"amount of entries": 0,
						"is empty": ["yes", {}],
						"is editable": is_editable,
						modified: ["no", {}],
						entries: {},
						"last created entry": "none",
						joined: "no",
						"has ordered graphs": has_ordered_graphs,
					},
				],
			},
		],
	};
}

function createCommandAttribute(): api_cs.Tattributes {
	return {
		type: [
			"operation",
			{
				type: [
					"command",
					{
						"execute permitted": "no",
					},
				],
				started: ["no", {}],
			},
		],
	};
}

function createActionAttribute(): api_cs.Tattributes {
	return {
		type: [
			"operation",
			{
				type: "action",
				started: ["no", {}],
			},
		],
	};
}

export function addTextAttribute(
	definition: api_app.Ctext__type__property,
	node: api_cs.Cnode
): api_cs.Ctext__type__property {
	const name = definition.parent.parent.key;
	let attribute = node.properties.attributes.get(name);
	if (!attribute) {
		const raw_attribute = createTextAttribute(definition, node.properties.location.state.name);
		attribute = node.properties.attributes.create(name, raw_attribute);
	}
	return attribute.properties.type.cast("property").properties.type.cast("text");
}

export function addNumberAttribute(
	definition: api_app.Cnumber__type__property,
	node: api_cs.Cnode
): api_cs.Cnumber__type__property {
	const name = definition.parent.parent.key;
	let attribute = node.properties.attributes.get(name);
	if (!attribute) {
		const raw_attribute = createNumberAttribute(definition, node.properties.location.state.name);
		attribute = node.properties.attributes.create(name, raw_attribute);
	}
	return attribute.properties.type.cast("property").properties.type.cast("number");
}

export function addFileAttribute(definition: api_app.Cfile, node: api_cs.Cnode): api_cs.Cfile__type__property {
	const name = definition.parent.parent.key;
	let attribute = node.properties.attributes.get(name);
	if (!attribute) {
		const raw_attribute = createFileAttribute(definition, node.properties.location.state.name);
		attribute = node.properties.attributes.create(name, raw_attribute);
	}
	return attribute.properties.type.cast("property").properties.type.cast("file");
}

export function addGroupAttribute(
	definition: api_app.Cgroup__type__property,
	node: api_cs.Cnode
): api_cs.Cgroup__type__property {
	const name = definition.parent.parent.key;
	let attribute = node.properties.attributes.get(name);
	if (!attribute) {
		const raw_attribute = createGroupAttribute(
			definition,
			node.properties.location.state.name,
			get_parent_location(node)
		);
		attribute = node.properties.attributes.create(name, raw_attribute);
	}
	return attribute.properties.type.cast("property").properties.type.cast("group");
}

export function addStategroupAttribute(
	definition: api_app.Cstate_group,
	node: api_cs.Cnode,
	remote_state?: api_app.Cstates__state_group
): api_cs.Cstate_group__type__property {
	const name = definition.parent.parent.key;
	let attribute = node.properties.attributes.get(name);
	if (!attribute) {
		const raw_stategroup = createStategroupAttribute(
			definition,
			node.properties.location.state.name,
			get_parent_location(node),
			remote_state
		);
		attribute = node.properties.attributes.create(name, raw_stategroup);
	}
	return attribute.properties.type.cast("property").properties.type.cast("state group");
}

export function addStateAttributes(state: api_cs.Cstates__state_group__type__property): void {
	switch (state.properties.location.state.name) {
		case "local": {
			const definition = state.key.ref.properties.node;
			const state_node = state.properties.node;
			for (const attribute of definition.properties.attributes.values()) {
				switch (attribute.properties.type.state.name) {
					case "property": {
						const property = attribute.properties.type.state.node;
						const kind = property.properties.type.state.node.output.value_source();
						switch (kind.variant.name) {
							case "base": {
								switch (property.properties.type.state.name) {
									case "collection":
										addCollectionAttribute(property.properties.type.state.node, state_node);
										break;
									case "file":
										addFileAttribute(property.properties.type.state.node, state_node);
										break;
									case "group":
										addGroupAttribute(property.properties.type.state.node, state_node);
										break;
									case "number":
										addNumberAttribute(property.properties.type.state.node, state_node);
										break;
									case "state group":
										addStategroupAttribute(property.properties.type.state.node, state_node);
										break;
									case "text":
										addTextAttribute(property.properties.type.state.node, state_node);
										break;
									default:
										unexpectedState(property.properties.type.state);
								}
								break;
							}
							case "derived":
								break;
							default:
								unexpectedState(kind.variant);
						}
						break;
					}
					case "action":
						break;
					case "command":
						break;
					case "reference set":
						break;
					default:
						unexpectedState(attribute.properties.type.state);
				}
			}
			break;
		}
		case "remote":
			break;
		default:
			unexpectedState(state.properties.location.state);
	}
}

export function addCollectionAttribute(
	definition: api_app.Ccollection__type__property,
	node: api_cs.Cnode
): api_cs.Ccollection {
	const name = definition.parent.parent.key;
	let attribute = node.properties.attributes.get(name);
	if (!attribute) {
		const raw_collection = createCollectionAttribute(definition, node.properties.location.state.name);
		attribute = node.properties.attributes.create(name, raw_collection);
	}
	return attribute.properties.type.cast("property").properties.type.cast("collection");
}

export function addCommandAttribute(
	definition: api_app.Cglobal,
	node: api_cs.Cnode
): api_cs.Coperation__type__attributes {
	const name = definition.parent.parent.key;
	let attribute = node.properties.attributes.get(name);
	if (!attribute) {
		attribute = node.properties.attributes.create(name, createCommandAttribute());
	}
	return attribute.properties.type.cast("operation");
}

export function addActionAttribute(
	definition: api_app.Caction,
	node: api_cs.Cnode
): api_cs.Coperation__type__attributes {
	const name = definition.parent.key;
	let attribute = node.properties.attributes.get(name);
	if (!attribute) {
		attribute = node.properties.attributes.create(name, createActionAttribute());
	}
	return attribute.properties.type.cast("operation");
}

export function addRemoteEntry(collection: api_cs.Ccollection, key: string): api_cs.Centries__collection {
	let entry = getInitializedEntryWithKey(collection, key);
	if (entry) {
		return entry;
	} else {
		const collection_definition = collection.inferences.collection_type();
		const key_property_name = collection_definition.properties.key_property.entry;
		const raw_node = createEmptyRemoteNode(collection_definition.properties.node);
		const raw_key_property = createTextAttribute(collection_definition.properties.key_property.ref, "remote", key);
		raw_node["attributes"][key_property_name] = raw_key_property;
		const internal_key = uuid.generate();
		entry = collection.properties.entries.create(internal_key, {
			location: [
				"remote",
				{
					"marked for deletion": ["no", {}],
				},
			],
			key: key_property_name,
			style: { initialized: "no" },
			node: raw_node,
		});
		iCollection(collection).addServerEntry(entry);
		return entry;
	}
}

export function addLocalEntry(
	collection: api_cs.Ccollection,
	by: "user" | "engine" = "user",
	key?: string
): api_cs.Centries__collection {
	const collection_definition = collection.inferences.collection_type();
	const key_property_name = collection_definition.properties.key_property.entry;
	const raw_node = createLocalNode(
		collection_definition.properties.node,
		collection.parent.parent.parent.properties.location.state.name
	);
	const raw_key_property = createTextAttribute(collection_definition.properties.key_property.ref, "local");
	raw_node["attributes"][key_property_name] = raw_key_property;
	const internal_key = uuid.generate();
	const entry = collection.properties.entries.create(internal_key, {
		location: [
			"local",
			{
				"modified by": [by, {}],
			},
		],
		key: key_property_name,
		style: { initialized: "no" },
		node: raw_node,
	});
	collection.properties.last_created_entry.set("entry", {
		entry: internal_key,
	});
	if (key !== undefined) {
		entry.properties.key.ref.properties.is_set.set("yes", {
			text: key,
			by: [by, {}],
		});
		entry.properties.key.ref.properties.is_editable.set("yes", {
			modified: [
				"yes",
				{
					value: key,
					saving: ["no", {}],
				},
			],
			"edit mode": "off",
		});
	}
	return entry;
}
